import {
    Button,
    Chip,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import { DYNAMIC_FEATURE_ID_FIELD_NAME } from "./constants";
import { INPUT_VALIDATION } from "../../constants";
import { PARCELLAIRE_REDUCER_ACTIONS } from "./parcellaireReducer";
import PropTypes from "prop-types";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { isScalablePerAxis } from "./utils";
import { useForm } from "react-hook-form";

const SCALE_PRESETS = [
    { label: "/4", value: 25 },
    { label: "/2", value: 50 },
    { label: "x1", value: 100 },
    { label: "x1.5", value: 150 },
    { label: "x2", value: 200 },
    { label: "x4", value: 400 },
];

export const ParcellaireScalePlots = ({
    onClose,
    parcellaireState,
    parcellaireDispatch,
    setGeoJsonKey,
}) => {
    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: {
            scaleFactorWidth: 100,
            scaleFactorHeight: 100,
        },
    });

    const currentWidth = watch("scaleFactorWidth");
    const currentHeight = watch("scaleFactorHeight");

    const onSubmit = (payload) => {
        if (
            payload.scaleFactorWidth === 100 &&
            payload.scaleFactorHeight === 100
        ) {
            onClose();
            return;
        }

        parcellaireDispatch({
            type: PARCELLAIRE_REDUCER_ACTIONS.RESIZE,
            scaleFactorWidth: payload.scaleFactorWidth,
            scaleFactorHeight: payload.scaleFactorHeight,
        });

        setGeoJsonKey(new Date().getTime());
        onClose();
    };

    const handleResetScale = () => {
        parcellaireDispatch({
            type: PARCELLAIRE_REDUCER_ACTIONS.RESET_SCALE,
        });

        setGeoJsonKey(new Date().getTime());
        onClose();
    };

    const selectedCount = parcellaireState.selectedLayers.length;

    const nonQuadrilateralCount = parcellaireState.selectedLayers.filter(
        (layer) =>
            layer.feature.geometry.type === "Polygon" &&
            !isScalablePerAxis(layer.feature)
    ).length;

    const hasScaledPlots = parcellaireState.selectedLayers.some(
        (layer) =>
            parcellaireState.scaleMap[
                layer.feature.properties[DYNAMIC_FEATURE_ID_FIELD_NAME]
            ] != null
    );

    const scaleValidation = {
        required: INPUT_VALIDATION.REQUIRED,
        valueAsNumber: true,
        validate: (value) =>
            (value > 0 && value <= 1000) ||
            "Must be greater than 0 and at most 1000",
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <DialogTitle>Scale Selected Plots</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        Scale factor is relative to the current size.
                        <br />
                        100% = no change, 200% = double, 50% = half.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Plot angles are preserved whenever possible.
                    </Typography>
                    {nonQuadrilateralCount > 0 && (
                        <Typography variant="body2" color="text.secondary">
                            {nonQuadrilateralCount} plot
                            {nonQuadrilateralCount !== 1 ? "s" : ""} that{" "}
                            {nonQuadrilateralCount !== 1 ? "are" : "is"} not a
                            simple quadrilateral will be scaled uniformly
                            (average of width/height) to preserve their shape.
                        </Typography>
                    )}
                    <Typography variant="subtitle2">Width</Typography>
                    <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        useFlexGap
                    >
                        {SCALE_PRESETS.map((preset) => (
                            <Chip
                                key={preset.value}
                                label={preset.label}
                                variant={
                                    Number(currentWidth) === preset.value
                                        ? "filled"
                                        : "outlined"
                                }
                                color={
                                    Number(currentWidth) === preset.value
                                        ? "primary"
                                        : "default"
                                }
                                onClick={() =>
                                    setValue("scaleFactorWidth", preset.value)
                                }
                            />
                        ))}
                    </Stack>
                    <TextField
                        label="Width scale (%)"
                        type="number"
                        fullWidth
                        error={Boolean(errors.scaleFactorWidth)}
                        helperText={errors.scaleFactorWidth?.message}
                        {...register("scaleFactorWidth", scaleValidation)}
                    />
                    <Typography variant="subtitle2">Height</Typography>
                    <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        useFlexGap
                    >
                        {SCALE_PRESETS.map((preset) => (
                            <Chip
                                key={preset.value}
                                label={preset.label}
                                variant={
                                    Number(currentHeight) === preset.value
                                        ? "filled"
                                        : "outlined"
                                }
                                color={
                                    Number(currentHeight) === preset.value
                                        ? "primary"
                                        : "default"
                                }
                                onClick={() =>
                                    setValue("scaleFactorHeight", preset.value)
                                }
                            />
                        ))}
                    </Stack>
                    <TextField
                        label="Height scale (%)"
                        type="number"
                        fullWidth
                        error={Boolean(errors.scaleFactorHeight)}
                        helperText={errors.scaleFactorHeight?.message}
                        {...register("scaleFactorHeight", scaleValidation)}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                {hasScaledPlots && (
                    <Button
                        variant="outlined"
                        color="warning"
                        onClick={handleResetScale}
                        startIcon={<RestartAltIcon />}
                    >
                        Reset
                    </Button>
                )}
                <Button type="submit" variant="contained">
                    Apply to {selectedCount} plot
                    {selectedCount !== 1 ? "s" : ""}
                </Button>
            </DialogActions>
        </form>
    );
};

ParcellaireScalePlots.propTypes = {
    onClose: PropTypes.func.isRequired,
    parcellaireState: PropTypes.object.isRequired,
    parcellaireDispatch: PropTypes.func.isRequired,
    setGeoJsonKey: PropTypes.func.isRequired,
};
